import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShield } from '@fortawesome/pro-solid-svg-icons/faShield';

import { BookingListRow } from 'us-react-components/dist/BookingListRow/BookingListRow';
import { BookingSuccessModal } from 'us-react-components/dist/BookingSuccessModal/BookingSuccessModal';
import {
  UserNameFilter,
  UserNameFilterOption,
} from 'us-react-components/dist/UserNameFilter/UserNameFilter';
import { Booking, BookingService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { ModalName } from '../../modals/constants';
import { useModal } from '../../modals/modalContext';

const CAREGIVER_TYPE_AGENCY = 2;

type FilterCode = 'all_active' | 'all_date' | 'completed' | 'expired_cancel' | 'rejected' | 'pending';

const FILTER_TYPES: Array<{
  code: FilterCode;
  labelKey: string;
  nameKey: string;
}> = [
  { code: 'all_active', labelKey: 'pages.bookingList.types.allActive', nameKey: 'pages.bookingList.typeNames.allActive' },
  { code: 'all_date', labelKey: 'pages.bookingList.types.allDate', nameKey: 'pages.bookingList.typeNames.allDate' },
  { code: 'completed', labelKey: 'pages.bookingList.types.completed', nameKey: 'pages.bookingList.typeNames.completed' },
  { code: 'expired_cancel', labelKey: 'pages.bookingList.types.expiredCancel', nameKey: 'pages.bookingList.typeNames.expiredCancel' },
  { code: 'rejected', labelKey: 'pages.bookingList.types.rejected', nameKey: 'pages.bookingList.typeNames.rejected' },
  { code: 'pending', labelKey: 'pages.bookingList.types.pending', nameKey: 'pages.bookingList.typeNames.pending' },
];



interface BookingWithStatus extends Booking {
  status?: Booking['status'] & {
    code?: string;
    label?: string;
    webIcon?: string;
    parentActions?: string[];
  };
  caregiver?: Booking['caregiver'] & { active?: boolean };
  parent?: Booking['parent'] & { active?: boolean };
  groupId?: number;
  group?: { label?: string };
}

export const BookingList = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { services: { tracker }, stores } = useDependencies();
  const modal = useModal();
  const user = stores.user.user;

  const isParent = !!user?.parent;

  // ---- Success modal wiring (?success=single|agency|recurring) ----
  const successParam = searchParams.get('success');
  const [singleSuccessOpen, setSingleSuccessOpen] = useState(false);

  const clearSuccessParam = (): void => {
    setSearchParams(
      (sp) => {
        const n = new URLSearchParams(sp);
        n.delete('success');
        return n;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (!successParam) return;
    if (successParam === 'single') {
      setSingleSuccessOpen(true);
    } else if (successParam === 'agency') {
      modal?.setModalName(ModalName.BookingAgencySuccess);
      clearSuccessParam();
    } else if (successParam === 'recurring') {
      modal?.setModalName(ModalName.BookingRecurringSuccess);
      clearSuccessParam();
    }

  }, [successParam]);

  const initialType = (searchParams.get('filterType') as FilterCode | null) ?? 'all_active';
  const [selectedType, setSelectedType] = useState<FilterCode>(initialType);
  const [bookings, setBookings] = useState<{
    data: BookingWithStatus[];
    meta?: { size?: number; pageCount?: number };
  }>({ data: [] });
  const [isFiltering, setIsFiltering] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [page, setPage] = useState<number>(Number(searchParams.get('page') || 1) || 1);
  const [totalBookings, setTotalBookings] = useState<number>(0);

  // Name filter (Caregiver for parent viewer, Family for sitter viewer)
  const initialNameQuery = ((): string => {
    const fromUrl = searchParams.get(isParent ? 'sitterId' : 'parentId');
    return fromUrl ? '' : '';
  })();

  const filter = useMemo(() => FILTER_TYPES.find((f) => f.code === selectedType) ?? FILTER_TYPES[0], [selectedType]);

  useEffect(() => {
    document.title = 'UrbanSitter - Bookings';
  }, []);

  const loadBookings = async (nextPage: number): Promise<void> => {
    if (!user?.id) return;
    setIsFiltering(true);
    setLoadError(false);
    const params: Record<string, unknown> = {
      filterType: selectedType,
      page: nextPage,
      userId: user.id,
    };
    if (isParent) {
      params.parentId = user.id;
    } else {
      params.sitterId = user.id;
    }
    const sitterId = searchParams.get('sitterId');
    const parentId = searchParams.get('parentId');
    if (sitterId) params.sitterId = sitterId;
    if (parentId) params.parentId = parentId;
    try {
      const resp = (await BookingService.getByFilter({ params })).data;
      const data = (resp?.data ?? []) as BookingWithStatus[];
      setBookings({ data, meta: resp?.meta });
      if (selectedType !== 'all_date' && data.length === 0) {
        loadTotalBookings();
      }
    } catch {
      setLoadError(true);
      setBookings({ data: [] });
    } finally {
      setIsFiltering(false);
    }
  };

  const loadTotalBookings = async (): Promise<void> => {
    if (!user?.id) return;
    try {
      const params: Record<string, unknown> = { userId: user.id, limit: 1 };
      if (isParent) params.parentId = user.id;
      else params.sitterId = user.id;
      const resp = (await BookingService.getByFilter({ params })).data;
      setTotalBookings(resp?.meta?.size ?? 0);
    } catch {
      setTotalBookings(0);
    }
  };

  useEffect(() => {
    loadBookings(page);

  }, [user?.id, selectedType, page]);

  const setType = (code: FilterCode): void => {
    setSelectedType(code);
    setPage(1);
    setSearchParams(
      (sp) => {
        const n = new URLSearchParams(sp);
        n.set('filterType', code);
        n.set('page', '1');
        return n;
      },
      { replace: true },
    );
  };

  const onFilterUserChange = (u: UserNameFilterOption | null): void => {
    const targetKey = isParent ? 'sitterId' : 'parentId';
    setSearchParams(
      (sp) => {
        const n = new URLSearchParams(sp);
        if (u?.id) n.set(targetKey, String(u.id));
        else n.delete(targetKey);
        n.set('page', '1');
        return n;
      },
      { replace: true },
    );
    setPage(1);
  };

  const setPageNum = (next: number): void => {
    setPage(next);
    setSearchParams(
      (sp) => {
        const n = new URLSearchParams(sp);
        n.set('page', String(next));
        return n;
      },
      { replace: true },
    );
  };

  // "Book Again" — mirrors Angular list-item.js rebookSitter(). Passes bookingId
  // through the wizard so AddDetailsStep can pre-populate from the prior booking.
  const onRebookSitter = (booking: BookingWithStatus): void => {
    const sitterId = booking.caregiver?.id;
    if (!sitterId) return;
    const cookies = new Cookies();
    cookies.set(
      'BOOKING_ORIGIN',
      `/app/bookings?${new URLSearchParams(window.location.search).toString()}`,
      { path: '/' },
    );
    tracker.track('booking-list', { action: 'rebook-sitter', sitterId });
    navigate(`/parent/bookings/frequency?sitterId=${sitterId}&bookingId=${booking.id}`);
  };

  const subtitle = isParent ? t('pages.bookingList.subtitleParent') : t('pages.bookingList.subtitleSitter');

  const emptyFilterLabel = selectedType !== 'all_active' ? `${t(filter.nameKey)} ` : '';

  const totalPages = bookings.meta?.pageCount ?? 1;

  return (
    <div className='container py-8'>
      <h1 className='text-2xl font-semibold mb-1'>{t('pages.bookingList.heading')}</h1>
      <p className='mb-5 text-sm'>{subtitle}</p>

      <div className='grid grid-cols-1 sm:grid-cols-12 gap-4 mb-4'>
        <div className='sm:col-span-4 md:col-span-3'>
          <h3 className='text-base font-semibold mb-1'>
            <label htmlFor='typeSelect'>{t('pages.bookingList.typeLabel')}</label>
          </h3>
          <select
            id='typeSelect'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            value={selectedType}
            onChange={(e): void => setType(e.target.value as FilterCode)}
          >
            {FILTER_TYPES.map((ft) => (
              <option key={ft.code} value={ft.code}>
                {t(ft.labelKey)}
              </option>
            ))}
          </select>
        </div>
        <div className='sm:col-span-4 md:col-span-3'>
          <UserNameFilter
            kind={isParent ? 'sitter' : 'parent'}
            viewerId={user?.id}
            label={isParent ? 'Caregiver:' : 'Family:'}
            initialValue={initialNameQuery}
            onSelect={onFilterUserChange}
          />
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-12 gap-4'>
        <div className='md:col-span-9'>
          {isFiltering && (
            <h2 className='text-base'>
              <i className='fa-solid fa-spinner fa-spin mr-2' />
              {t('pages.bookingList.loading')}
            </h2>
          )}

          {!isFiltering && loadError && (
            <div className='us-card text-center p-6 rounded'>
              <h3 className='mb-1 font-semibold'>{t('common.errors.loadFailedTitle')}</h3>
              <p className='mb-3'>{t('common.errors.loadFailedBody')}</p>
              <Button
                buttonType={ButtonType.primary}
                onClick={(): void => { void loadBookings(page); }}
              >
                {t('common.errors.retry')}
              </Button>
            </div>
          )}

          {!isFiltering && !loadError && bookings.data.map((booking) => (
            <BookingRow
              key={booking.id}
              booking={booking}
              isParent={isParent}
              onRebookSitter={onRebookSitter}
            />
          ))}

          {!isFiltering && !loadError && bookings.data.length === 0 && (
            <div className='us-card text-center p-6 rounded'>
              <h3 className='mb-1 font-semibold'>
                {t('pages.bookingList.emptyTitle', { filter: emptyFilterLabel, withName: '' })}
              </h3>
              {selectedType === 'all_active' && (
                <p>
                  {isParent ? (
                    <>
                      {t('pages.bookingList.emptyParent')}{' '}
                      <a id='null-search-sitters' href='/app/sitters' className='underline'>
                        {t('pages.bookingList.searchCaregivers')}
                      </a>
                    </>
                  ) : (
                    <>
                      {t('pages.bookingList.emptySitter')}{' '}
                      <a id='null-job-board' href='/app/job-board' className='underline'>
                        {t('pages.bookingList.jobBoardLink')}
                      </a>
                    </>
                  )}
                </p>
              )}
              {totalBookings > 0 && selectedType !== 'all_date' && (
                <Button
                  buttonType={ButtonType.tertiary}
                  id='all-bookings-with-name'
                  onClick={(): void => setType('all_date')}
                  className='cursor-pointer underline inline-block mt-3 bg-transparent border-0 p-0'
                >
                  {t('pages.bookingList.seeAll')}
                </Button>
              )}
            </div>
          )}

          {!isFiltering && totalPages > 1 && (
            <div className='text-center mt-5'>
              <Button
                buttonType={ButtonType.secondary}
                className='btn btn-secondary mr-2'
                disabled={page <= 1}
                onClick={(): void => setPageNum(page - 1)}
              >
                Previous
              </Button>
              <span>
                Page {page} of {totalPages}
              </span>
              <Button
                buttonType={ButtonType.secondary}
                className='btn btn-secondary ml-2'
                disabled={page >= totalPages}
                onClick={(): void => setPageNum(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
        <aside className='md:col-span-3'>
          <KeepOnPlatformSidebar isParent={isParent} userId={user?.id} />
        </aside>
      </div>

      <BookingSuccessModal
        isOpen={singleSuccessOpen}
        onClose={(): void => {
          setSingleSuccessOpen(false);
          clearSuccessParam();
        }}
      />
    </div>
  );
});

interface RowProps {
  booking: BookingWithStatus;
  isParent: boolean;
  onRebookSitter: (booking: BookingWithStatus) => void;
}

const BookingRow = ({ booking, isParent, onRebookSitter }: RowProps): JSX.Element => {
  const { t } = useTranslation();
  const contact = isParent ? booking.caregiver : booking.parent;
  const active = contact?.active !== false;
  const isAgencyBooking = booking.caregiverType === CAREGIVER_TYPE_AGENCY;
  const contactRoute = isParent ? 'sitter' : 'parent';

  // Angular list-item.tpl.html: show "Book Again" for all parent bookings
  // (ng-if="$ctrl.isParent") — not restricted to closed/rebookable statuses.
  const showRebook = isParent && !isAgencyBooking && active && !!booking.caregiver?.id;

  const cta: { id: string; label: string; url: string; link?: boolean } | null = ((): { id: string; label: string; url: string; link?: boolean } | null => {
    if (isParent) {
      if (booking.status?.parentActions?.includes('parent_confirm_payment')) {
        return {
          id: `pay-sitter-${booking.id}`,
          label: t('pages.bookingList.payCaregiver'),
          url: `/app/bookings/${booking.id}/confirm-payment`,
        };
      }
      if (booking.group?.label) {
        return {
          id: `booking-group-${booking.groupId}`,
          link: true,
          label: booking.group.label,
          url: `/app/booking-group/${booking.groupId}/summary`,
        };
      }
    } else if (booking.status?.code === 'job_finished' && booking.paymentType === 'credit') {
      return {
        id: `confirm-hours-${booking.id}`,
        label: t('pages.bookingList.confirmJobDetails'),
        url: `/app/bookings/${booking.id}/confirm-hours`,
      };
    }
    return null;
  })();

  return (
    <BookingListRow
      bookingId={booking.id ?? ''}
      contact={contact}
      statusLabel={booking.status?.label}
      statusType={booking.status?.type}
      statusWebIcon={booking.status?.webIcon}
      start={booking.start}
      end={booking.end}
      isAgencyBooking={isAgencyBooking}
      contactHref={active && !isAgencyBooking ? `/app/${contactRoute}/${contact?.id}` : undefined}
      viewDetailsHref={`/app/bookings/${booking.id}`}
      primaryAction={showRebook ? (
        <Button
          buttonType={ButtonType.primary}
          id='rebook-sitter'
          className='btn btn-primary btn-block w-full'
          onClick={(): void => onRebookSitter(booking)}
        >
          {t('pages.bookingList.bookAgain', { defaultValue: 'Book Again' })}
        </Button>
      ) : undefined}
      ctaAction={cta && active ? (
        <a
          id={cta.id}
          href={cta.url}
          className={cta.link ? 'underline text-sm' : 'btn btn-secondary btn-block w-full'}
        >
          {cta.label}
        </a>
      ) : undefined}
      viewDetailsLabel={t('pages.bookingList.viewDetails')}
      noLongerActiveLabel={t('pages.bookingList.noLongerActive')}
    />
  );
};

// Ports Angular keep-on-platform-sidebar with role-specific items and the
// sitter "Add a job" CTA.
const KeepOnPlatformSidebar = ({
  isParent,
  userId,
}: {
  isParent: boolean;
  userId?: number;
}): JSX.Element => {
  const parentItems = [
    {
      title: 'Convenience',
      text: 'Skip the ATM and pay with just a click. Handy job reminders keep caregivers organized and on-time.',
    },
    {
      title: 'Peace of mind',
      text: "Our team works around the clock to ensure your family's safety. Book with us every time so we can help if needed.",
    },
    {
      title: 'Invite Friends – Get $25',
      text: userId
        ? `Refer friends to UrbanSitter and earn your way to a free night out! <a href="/app/parent/${userId}/referral" class="underline">See details</a>.`
        : 'Refer friends to UrbanSitter and earn your way to a free night out!',
    },
  ];

  const sitterItems = [
    {
      title: 'Peace of mind',
      text: 'Our Support Team can only offer their assistance with jobs booked on UrbanSitter– handy if you ever run into a tricky payment situation.',
    },
    {
      title: 'Badges and reviews = more jobs',
      text: 'Earn a Repeat Family badge to boost your profile. And, we automatically remind families to review you.',
    },
    {
      title: 'Reminders',
      text: 'UrbanSitter can help both you and the family keep track of jobs and payment.',
    },
  ];

  const items = isParent ? parentItems : sitterItems;

  return (
    <div className='us-card rounded p-4'>
      <div className='flex items-center gap-2 mb-3'>
        <FontAwesomeIcon icon={faShield} className='text-sapphire' />
        <h3 className='text-base font-semibold mb-0'>Why keep jobs on UrbanSitter?</h3>
      </div>
      <div className='space-y-3 text-sm'>
        {items.map((item) => (
          <div key={item.title}>
            <div className='font-semibold'>{item.title}</div>
            <div
              className='text-neutral-600'
              dangerouslySetInnerHTML={{ __html: item.text }}
            />
          </div>
        ))}
      </div>
      {!isParent && (
        <a
          id='sitter-add-job-side'
          href='/app/sitter/booking/create'
          className='btn btn-primary btn-block w-full mt-4 text-center'
        >
          Add a job
        </a>
      )}
    </div>
  );
};
